import * as angular from 'angular';
import * as _ from 'lodash';

import { ApprovalSettings, Dashboard, Dictionary, Group, GroupConfigMode, IGuid, IValueViewModel } from '../../models';
import { IStoreService, ITrendService, IWidgetSerializationService } from '..';
import { IValidationPageConfigurationService } from '../data';
import { deepTransform, toDictionary } from '../../common';
import { IWidget } from '../../widgets';

export type StatusUpdate = (msg: string) => void;

export interface IIdentifierCascadeDeleteService {
    deleteIdentifier(
        item: IValueViewModel,
        dashboards: Dictionary<Dashboard>,
        viewModels: Dictionary<IValueViewModel>,
        groups: Dictionary<Group>,
        approvalSettings: Dictionary<ApprovalSettings>,
        updateStatus?: StatusUpdate
    ): angular.IPromise<void>;
}

export class IdentifierCascadeDeleteService implements IIdentifierCascadeDeleteService {
    static serviceId = 'identifierCascadeDeleteService';

    static $inject = [
        '$q',
        'storeService',
        'trendService',
        'validationPageConfigurationService',
        'widgetSerializationService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _storeService: IStoreService,
        private _trendService: ITrendService,
        private _validationPageConfigurationService: IValidationPageConfigurationService,
        private _widgetSerializationService: IWidgetSerializationService
    ) {
    }

    deleteIdentifier(
        item: IValueViewModel,
        dashboards: Dictionary<Dashboard>,
        viewModels: Dictionary<IValueViewModel>,
        groups: Dictionary<Group>,
        approvalSettings: Dictionary<ApprovalSettings>,
        status?: StatusUpdate
    ): angular.IPromise<void> {
        const promises = [
            this.removeTrendsReference(item, status),
            this.removeDashboardsReference(item, dashboards, status),
            this.removeValidationPagesReference(item, viewModels, groups, status),
            this.removeApprovalSeries(item, approvalSettings, status)
        ];
        
        return this._$q
            .all(promises)
            .then(() => this._storeService.dispatchers().identifiers.deleteOne(item.groupList[0].guid, item.guid));
    }

    private removeTrendsReference(item: IValueViewModel, status?: StatusUpdate): angular.IPromise<void> {
        this.updateStatus('Loading trends', status);
        return this._trendService
            .getAll()
            .then(trends => {
                this.updateStatus('Deleting trends', status);
                const toUpdate = trends.filter(t => t.configuration.series.filter(s => item.guid === s.guid).length > 0);
                toUpdate.forEach(t => t.configuration.series = t.configuration.series.filter(s => item.guid !== s.guid));
                return this._$q.all(toUpdate.map(t => this._trendService.updateTrend(t)));
            })
            .then(() => this._$q.when());
    }

    private removeDashboardsReference(item: IValueViewModel, dashboards: Dictionary<Dashboard>, status?: StatusUpdate): angular.IPromise<void> {
        this.updateStatus('Saving dashboards', status);

        const working = angular.copy(dashboards) ?? {};
        const deserialize = (d: Dashboard) => d.configuration.widgets = this.deserializeAndStripServices(d.configuration.widgets);
        const filter = e => this.containsSeries(e, item.guid);
        const toUpdate = this.replaceIfContainsGuid(working, item.guid, filter, deserialize);

        return this._storeService.dispatchers().dashboards
            .updateMany(toUpdate)
            .then(() => this._$q.when());
    }

    private removeValidationPagesReference(
        item: IValueViewModel,
        viewModels: Dictionary<IValueViewModel>,
        allGroups: Dictionary<Group>,
        status?: StatusUpdate
    ): angular.IPromise<void> {
        let guids: string[];
        this.updateStatus('Loading validation pages', status);
        return this._validationPageConfigurationService
            // get all group with a validation page
            .getGroupsWithWidgets()
            .then(groups => {
                // split between type and group validation pages
                const byConfigMode = _.groupBy(groups, g => g.configMode);
                const byType = _.groupBy(byConfigMode[GroupConfigMode.ByType], g => g.type);
                guids = Object.keys(byType)
                    // take only first group by type, not to edit them multiple times
                    .map(k => byType[k][0])
                    .concat(byConfigMode[GroupConfigMode.ByGroup] ?? [])
                    .map(g => g.guid);
                return this._$q.all(guids.map(g => this._validationPageConfigurationService.get(g, viewModels, allGroups)));
            })
            .then(widgets => {
                // replace for each groups
                this.updateStatus('Saving group validation pages', status);
                const promises = guids
                    .map((g, i) => {
                        const deserialized = this.deserializeAndStripServices(widgets[i]);
                        return { guid: g, widgets: toDictionary(deserialized as IGuid[]) };
                    })
                    .filter(a => this.containsSeries(a.widgets, item.guid))
                    .map(a => {
                        const replaced = this.replaceIfContainsGuid(a.widgets, item.guid) as IWidget[];
                        return this._validationPageConfigurationService.update(a.guid, replaced);
                    });

                return promises.reduce(this._$q.when, this._$q.resolve());
            })
            .then(() => this._storeService.dispatchers().validationPageConfigurations.getAll(true))
            .then(() => this._$q.when());
    }

    private removeApprovalSeries(item: IValueViewModel, approvalSettings: Dictionary<ApprovalSettings>, status?: StatusUpdate) {
        this.updateStatus('Saving approval series', status);
        const toUpdate = Object.values(approvalSettings).filter(as => as.series.filter(s => item.guid === s.guid).length > 0);
        toUpdate.forEach(as => as.series = as.series.filter(s => item.guid !== s.guid));
        const promises = toUpdate.map(as => this._storeService.dispatchers().approvalSettings.update(as));
        return this._$q.all(promises);
    }

    private deserializeAndStripServices(widgets: IWidget[]) : IWidget[] {
        return this._widgetSerializationService
            .deserializeWidgets(widgets)
            .map(w => this._widgetSerializationService.stripServices(w));
    }

    private containsSeries<T>(e: T, guid: string): boolean {
        return (JSON.stringify(e).indexOf(guid) >= 0);
    }

    private replaceIfContainsGuid<T>(
        dictionary: Dictionary<T>,
        guid: string,
        filter?: (e: T) => boolean,
        apply?: (e: T) => void
    ): T[] {
        const guidRemover = (key, value, isObject) => isObject && value['guid'] === guid ? [] : [key, value];
        let values = _.values(dictionary);

        if (filter)
        {
            values = values.filter(filter);
        }

        if (apply)
        {
            values.forEach(apply);
        }

        return values.map(d => deepTransform(d, guidRemover) as T);
    }

    private updateStatus(msg: string, status?: StatusUpdate) {
        if (status)
        {
            status(msg);
        }
    }
}